import Button from '@mui/material/Button';
import React, { useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { FormattedMessage } from 'react-intl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import DialogActions from '@mui/material/DialogActions';
import { unwrapResult } from '@reduxjs/toolkit';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import { StyledMenu } from '../../menu/StyledMenu';
import { fromPaymentMethod, useAppDispatch } from '../../../store';
import { Account, PaymentMethod } from '../../../models';
import { useReadOnlyMode } from '../../../routes';

const useStyles = makeStyles(theme => ({
  actionButton: {
    fontWeight: 500,
    color: '#000000',
  },
  deleteDialogPaper: {
    paddingBottom: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
}));

export const PaymentMethodActionMenu = (
  { account, currentCard, loading, handleCancel, setLoading }:
  {
    account?: Account,
    currentCard?: PaymentMethod,
    loading: boolean,
    handleCancel: () => void,
    setLoading: (loading: boolean) => void,
  },
) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isReadOnly = useReadOnlyMode();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeletePaymentMethod = async () => {
    if (!currentCard) return;
    setLoading(true);
    await dispatch(fromPaymentMethod.doDeletePaymentMethod({
      id: currentCard!!.id,
    })).then(unwrapResult)
      .then(() => {
        handleCancel();
        handleCloseDeleteDialog();
        enqueueSnackbar(<FormattedMessage id="billinginfo.cardinfo.delete.success" />, { variant: 'success' });
      })
      .catch(() => {
        // do nothing
      })
      .finally(() => setLoading(false));
  };

  const handleCloseDeleteDialog = () => setIsDeleteDialogOpen(false);

  const renderDeleteCardDialog = () => (
    <Dialog
      open={isDeleteDialogOpen}
      onClose={() => setIsDeleteDialogOpen(false)}
      classes={{
        paper: classes.deleteDialogPaper,
      }}
    >
      <DialogTitle>
        <FormattedMessage id="billinginfo.delete_dialog.title" />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage
            id="billinginfo.delete_dialog.warning1"
            values={{ b: (name: string) => <strong>{name}</strong> }}
          />
        </Typography>
        <Typography>
          <FormattedMessage
            id="billinginfo.delete_dialog.warning2"
            values={{ accountName: account?.name }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCloseDeleteDialog}
          variant="contained"
          disabled={loading}
        >
          <FormattedMessage id="billinginfo.delete_dialog.cancel" />
        </Button>
        <Button
          onClick={handleDeletePaymentMethod}
          variant="contained"
          color="secondary"
          disabled={loading}
        >
          <FormattedMessage id="billinginfo.delete_dialog.delete" />
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <>
      <Button
        id="payment-method-action-btn"
        variant="outlined"
        className={classes.actionButton}
        aria-controls={open ? 'payment-method-action-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </Button>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{
          'aria-labelledby': 'delete-payment-method',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => setIsDeleteDialogOpen(true)}
          disableRipple
          disabled={isReadOnly}
        >
          <Box
            id="delete-payment-method-menu-item"
            display="flex"
            alignItems="center"
            alignContent="center"
            width="100%"
            color="#D14343"
          >
            <DeleteOutlineIcon sx={{ width: 24, height: 24, fill: '#D14343' }} />
            <Typography
              variant="body1"
            >
              <FormattedMessage id="billinginfo.button.delete" />
            </Typography>
          </Box>

        </MenuItem>
      </StyledMenu>
      {renderDeleteCardDialog()}
    </>
  );
};
